import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { DemoProvider } from './context/DemoContext';
import Layout from './components/Layout';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminInfluencers from './pages/AdminInfluencers';
import AdminInfluencerView from './pages/AdminInfluencerView';
import AdminVendors from './pages/AdminVendors';
import AdminProducts from './pages/AdminProducts';
import AdminProductDetail from './pages/AdminProductDetail';
import AdminProductForm from './pages/AdminProductForm';
import AdminCsvImport from './pages/AdminCsvImport';
import AdminRequests from './pages/AdminRequests';
import AdminWebsites from './pages/AdminWebsites';
import AdminWebsiteDetail from './pages/AdminWebsiteDetail';
import AdminWebsiteEditor from './pages/AdminWebsiteEditor';
import AdminCreatorMatch from './pages/AdminCreatorMatch';
import AdminExploreInfluencers from './pages/AdminExploreInfluencers';
import AdminManageUsers from './pages/AdminManageUsers';
import InfluencerDashboard from './pages/InfluencerDashboard';
import InfluencerExplore from './pages/InfluencerExplore';
import InfluencerRequests from './pages/InfluencerRequests';
import InfluencerProducts from './pages/InfluencerProducts';
import InfluencerYourSite from './pages/InfluencerYourSite';
import Settings from './pages/Settings';
import SetPassword from './pages/SetPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import VendorLogin from './pages/VendorLogin';
import VendorAcceptInvite from './pages/VendorAcceptInvite';
import VendorDashboard from './pages/VendorDashboard';
import VendorCreators from './pages/VendorCreators';
import VendorCreatorDetail from './pages/VendorCreatorDetail';
import VendorCreatorInsights from './pages/VendorCreatorInsights';
import VendorForecasts from './pages/VendorForecasts';
import VendorProducts from './pages/VendorProducts';
import VendorOrders from './pages/VendorOrders';
import VendorPayouts from './pages/VendorPayouts';
import VendorSettings from './pages/VendorSettings';
import VendorRegister from './pages/VendorRegister';
import VendorProductForm from './pages/VendorProductForm';
import VendorCsvImport from './pages/VendorCsvImport';
import VendorOrderDetail from './pages/VendorOrderDetail';
import Signup from './pages/Signup';
import RequestPasswordReset from './pages/RequestPasswordReset';
// ── Report-first surfaces ──
import Landing from './pages/Landing';
import ReportView from './pages/ReportView';
import Reports from './pages/Reports';
import NewReport from './pages/NewReport';
import Upgrade from './pages/Upgrade';
import LegalPage from './pages/Legal';

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
        <p className="text-ink-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// ── Layout route wrappers ────────────────────────────────────────────────
//
// Each wrapper renders ONE <Layout> instance that persists for the entire
// time the user is in that role's section. Because the <aside> (sidebar)
// DOM element is never destroyed during navigation, CSS :hover state is
// preserved and the sidebar never flashes closed/open on route changes.

/** Compute the canonical landing path for an authenticated user's role. */
function roleHome(role: string | undefined): string {
  if (role === 'admin') return '/admin/dashboard';
  if (role === 'vendor') return '/vendor/dashboard';
  if (role === 'influencer') return '/dashboard';
  return '/login';
}

/** When VITE_ADMIN_HOST is configured, all /admin/* routes redirect to the
 *  admin subdomain SPA instead of being served from the main bundle. */
function AdminHostRedirect() {
  const target = (import.meta.env.VITE_ADMIN_HOST as string | undefined)?.trim();
  if (target && typeof window !== 'undefined') {
    const path = window.location.pathname + window.location.search;
    window.location.replace(target.replace(/\/+$/, '') + path);
    return null;
  }
  return null;
}

function AdminLayoutRoute() {
  const { user, loading } = useAuth();
  const adminHost = (import.meta.env.VITE_ADMIN_HOST as string | undefined)?.trim();
  if (adminHost) return <AdminHostRedirect />;
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to={roleHome(user.role)} replace />;
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function InfluencerLayoutRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'influencer') return <Navigate to={roleHome(user.role)} replace />;
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

/** Auth-only wrapper for the fullscreen editor (no Layout/sidebar). */
function AdminAuthOnlyRoute() {
  const { user, loading } = useAuth();
  const adminHost = (import.meta.env.VITE_ADMIN_HOST as string | undefined)?.trim();
  if (adminHost) return <AdminHostRedirect />;
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to={roleHome(user.role)} replace />;
  return <Outlet />;
}

/** Auth-only wrapper for the full-bleed report viewer (any signed-in user). */
function AuthOnlyRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function VendorLayoutRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/vendor/login" replace />;
  if (user.role !== 'vendor') return <Navigate to={roleHome(user.role)} replace />;
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to={roleHome(user.role)} replace />;
  return <>{children}</>;
}

export default function App() {
  // NOTE: we intentionally do NOT block the entire app on
  // `useAuth().loading`. Each route wrapper (AdminLayoutRoute,
  // InfluencerLayoutRoute, VendorLayoutRoute, AdminAuthOnlyRoute,
  // AuthOnlyRoute, PublicRoute) checks `loading` itself, so protected
  // routes still wait for the session probe — but truly public routes
  // (`/`, `/legal/*`, `/reports/demo`, `/demo-report`) render
  // immediately. Otherwise an unauthenticated visitor would stare at a
  // spinner while `/api/auth/me` returns 401, which they may interpret
  // as a broken page.
  return (
    <DemoProvider>
    <Routes>
      {/* ── Public routes ── */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/admin/login" element={
        (import.meta.env.VITE_ADMIN_HOST as string | undefined)?.trim()
          ? <AdminHostRedirect />
          : <PublicRoute><AdminLogin /></PublicRoute>
      } />
      <Route path="/vendor/login" element={<PublicRoute><VendorLogin /></PublicRoute>} />
      <Route path="/vendor/register" element={<PublicRoute><VendorRegister /></PublicRoute>} />
      <Route path="/vendor/accept-invite" element={<PublicRoute><VendorAcceptInvite /></PublicRoute>} />
      <Route path="/accept-invite" element={<PublicRoute><AcceptInvite /></PublicRoute>} />
      <Route path="/invite" element={<PublicRoute><SetPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><RequestPasswordReset /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />

      {/* ── Fullscreen editor + admin report viewer — admin-auth only ── */}
      <Route element={<AdminAuthOnlyRoute />}>
        <Route path="/admin/websites/:influencerDbId/editor" element={<AdminWebsiteEditor />} />
        <Route path="/admin/reports/:id" element={<ReportView />} />
      </Route>

      {/* ── Admin routes — single persistent Layout instance ── */}
      <Route element={<AdminLayoutRoute />}>
        <Route path="/admin/dashboard"              element={<AdminDashboard />} />
        <Route path="/admin/influencers"            element={<AdminInfluencers />} />
        <Route path="/admin/influencers/:id/view"   element={<AdminInfluencerView />} />
        <Route path="/admin/vendors"                element={<AdminVendors />} />
        <Route path="/admin/products"                element={<AdminProducts />} />
        <Route path="/admin/products/new"           element={<AdminProductForm />} />
        <Route path="/admin/products/import-csv"    element={<AdminCsvImport />} />
        <Route path="/admin/products/:id"           element={<AdminProductDetail />} />
        <Route path="/admin/products/:id/edit"      element={<AdminProductForm />} />
        <Route path="/admin/vendor-products"        element={<Navigate to="/admin/products" replace />} />
        <Route path="/admin/catalog"                element={<Navigate to="/admin/products" replace />} />
        <Route path="/admin/requests"               element={<AdminRequests />} />
        <Route path="/admin/websites"                          element={<AdminWebsites />} />
        <Route path="/admin/websites/:influencerDbId"          element={<AdminWebsiteDetail />} />
        <Route path="/admin/creator-match"          element={<AdminCreatorMatch />} />
        <Route path="/admin/runs"                   element={<Navigate to="/admin/explore" replace />} />
        <Route path="/admin/explore"               element={<AdminExploreInfluencers />} />
        <Route path="/admin/manage-users"           element={<AdminManageUsers />} />
        <Route path="/settings"                     element={<Settings />} />
      </Route>

      {/* ── Vendor routes — single persistent Layout instance ── */}
      <Route element={<VendorLayoutRoute />}>
        <Route path="/vendor/dashboard"   element={<VendorDashboard />} />
        <Route path="/vendor/creators" element={<VendorCreators />} />
        <Route path="/vendor/creators/:id" element={<VendorCreatorDetail />} />
        <Route path="/vendor/creator-insights" element={<VendorCreatorInsights />} />
        <Route path="/vendor/forecasts" element={<VendorForecasts />} />
        <Route path="/vendor/products"            element={<VendorProducts />} />
        <Route path="/vendor/products/new"            element={<VendorProductForm />} />
        <Route path="/vendor/products/import-csv"    element={<VendorCsvImport />} />
        <Route path="/vendor/products/:id/edit"      element={<VendorProductForm />} />
        <Route path="/vendor/orders"              element={<VendorOrders />} />
        <Route path="/vendor/orders/:id"          element={<VendorOrderDetail />} />
        <Route path="/vendor/payouts" element={<VendorPayouts />} />
        <Route path="/vendor/inventory" element={<Navigate to="/vendor/products?tab=inventory" replace />} />
        <Route path="/vendor/returns" element={<Navigate to="/vendor/orders?tab=returns" replace />} />
        <Route path="/vendor/financials" element={<Navigate to="/vendor/payouts" replace />} />
        <Route path="/vendor/analytics" element={<Navigate to="/vendor/dashboard" replace />} />
        <Route path="/vendor/settings"    element={<VendorSettings />} />
      </Route>

      {/* ── Influencer routes — single persistent Layout instance ── */}
      <Route element={<InfluencerLayoutRoute />}>
        <Route path="/dashboard"     element={<InfluencerDashboard />} />
        <Route path="/reports"       element={<Reports />} />
        <Route path="/reports/new"   element={<NewReport />} />
        <Route path="/upgrade"       element={<Upgrade />} />
        <Route path="/explore"       element={<InfluencerExplore />} />
        <Route path="/requests"      element={<InfluencerRequests />} />
        <Route path="/products"      element={<InfluencerProducts />} />
        <Route path="/your-site"     element={<InfluencerYourSite />} />
        <Route path="/settings"      element={<Settings />} />
      </Route>

      {/* Public demo report — fully populated premium fixture. The
          literal `/reports/demo` segment ranks higher than the auth-
          gated `/reports/:id` param below, so anyone can hit it
          without signing in. `/demo-report` is a friendlier alias. */}
      <Route path="/reports/demo" element={<ReportView />} />
      <Route path="/demo-report" element={<Navigate to="/reports/demo" replace />} />

      {/* ── Full-bleed report viewer — auth required, no sidebar ── */}
      <Route element={<AuthOnlyRoute />}>
        <Route path="/reports/:id" element={<ReportView />} />
      </Route>

      {/* ── Default ── */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/legal/:kind" element={<LegalPage />} />
      <Route path="/legacy-home" element={<PublicRoute><Home /></PublicRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </DemoProvider>
  );
}
